/**
 * The dishes of a table, grouped by wave and then by category.
 *
 * Rendered by the card on the board AND by the full-screen view opened from its
 * header. That is the point of it being a component: the two cannot drift apart
 * and show a cook different things about the same table.
 */
import {
  bucketItemsByWave,
  formatElapsed,
  delayTier as computeDelayTier,
  sortItemsByWait,
} from '@shared/kds/table-board';
import type { KdsBoardConfig, DelayTier } from '@shared/kds/table-board';
import { groupKdsItems } from '@shared/kds/grouping';
import GroupedDishRow from './GroupedDishRow';
import { useClientConfig } from '../hooks/useClientConfig';
import { useKitchenStore } from '../stores/kitchen.store';
import { useTranslation } from '../i18n';

interface TableCardBodyProps {
  activeItems: any[];
  servedItems: any[];
  showServed: boolean;
  config: KdsBoardConfig;
  now: number;
  oldestItemId?: string | null;
  /**
   * No entrée block, no wave headers — just the active dishes, oldest first.
   * Used by single-station boards such as the bar, where waves mean nothing.
   */
  flat?: boolean;
  onOpenDish?: (item: any) => void;
  /**
   * Feature 252 — override for tests. Left alone, the switch is read from the
   * venue configuration, which is refreshed every few seconds: flipping it
   * redraws open tables without anyone restarting anything.
   */
  groupIdenticalDishes?: boolean;
}

export default function TableCardBody({
  activeItems,
  servedItems,
  showServed,
  config,
  now,
  oldestItemId = null,
  flat = false,
  onOpenDish,
  groupIdenticalDishes,
}: TableCardBodyProps) {
  const { t } = useTranslation();
  const clientId = useKitchenStore((st: any) => st.clientId);
  const { clientConfig } = useClientConfig(clientId);
  // Read on every render, never captured in state: that is what makes the
  // switch reach a table that is already open (FR-013).
  const grouping = groupIdenticalDishes ?? clientConfig?.groupIdenticalDishes === true;
  // Feature 268 — same source, same render-time read, so flipping the switch
  // reaches a table card already on screen.
  const showDinerTag = clientConfig?.dinerTagOnServiceEnabled === true;
  const { entree, waves } = bucketItemsByWave(activeItems, config, t);

  /**
   * One list of dishes, drawn. With grouping off this yields exactly one row per
   * portion, in the original order, which is the behaviour that predates the
   * feature; with it on, matching portions share a line carrying their count.
   *
   * Grouping happens per list, and every list here belongs to ONE order: two
   * portions fired on different orders must never share a line, or the later one
   * would be sent out with plates already on the pass.
   */
  const rows = (items: any[], served?: boolean) =>
    groupKdsItems(items, grouping).map((line) => (
      <GroupedDishRow
        showDinerTag={showDinerTag}
        key={line.portions[0].id}
        line={line}
        delayTier={
          served ? undefined : (computeDelayTier(line.portions[0], now, config) as DelayTier)
        }
        isOldest={!served && line.portions.some((p: any) => p.id === oldestItemId)}
        now={now}
        served={served}
        onOpenDish={onOpenDish}
      />
    ));

  if (flat) {
    return (
      <div className="space-y-1.5">
        {groupKdsItems(sortItemsByWait(activeItems), grouping).map((line) => (
          <GroupedDishRow
            showDinerTag={showDinerTag}
            key={line.portions[0].id}
            line={line}
            delayTier={computeDelayTier(line.portions[0], now, config) as DelayTier}
            isOldest={line.portions.some((p: any) => p.id === oldestItemId)}
            now={now}
            bar
            onOpenDish={onOpenDish}
          />
        ))}
      </div>
    );
  }

  if (waves.length === 0 && !entree) {
    return (
      <p className="text-kds-muted text-kds-meta text-center py-4">{t('kds.table.nothingToPrepare', 'Nessun piatto da preparare')}</p>
    );
  }

  return (
    <>
      {/* The welcome course, above the waves and on its own. */}
      {entree && (
        <div className="rounded-xl border border-kds-muted/30 bg-kds-card/30 overflow-hidden">
          <div className="flex items-center gap-2 px-2.5 py-1 bg-kds-muted/15 border-l-4 border-kds-muted">
            <span className="text-kds-badge font-black uppercase tracking-wider text-kds-text">
              {t('kds.table.entree', 'Entré')}
            </span>
          </div>
          <div className="space-y-1.5 p-1.5">{rows(entree.items)}</div>
        </div>
      )}

      {waves.map((wave) => (
        <div
          key={wave.wave}
          className="rounded-xl border-2 border-kds-priority/40 bg-kds-card/30 overflow-hidden"
        >
          <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-kds-priority/20 border-l-4 border-kds-priority">
            <span className="text-kds-action font-black uppercase tracking-wider text-kds-text leading-none">
              {t('kds.wave.ordinalLabel', '{{position}}ª ondata', { position: wave.positionIndex + 1 })}
            </span>
            {wave.oldestTs !== null ? (
              <span
                className="text-kds-action tabular-nums text-kds-text leading-none"
                title={t('kds.table.waveOldestTimer', "Tempo dal piatto firato più vecchio dell'ondata")}
              >
                ⏱ {formatElapsed(now - wave.oldestTs)}
              </span>
            ) : (
              <span
                className="text-kds-meta text-kds-muted italic leading-none"
                title={t('kds.table.waveNotSent', 'Ondata non ancora inviata in cucina: il cronometro parte alla chiamata')}
              >
                {t('kds.table.waiting', '⏳ In attesa')}
              </span>
            )}
          </div>

          <div className="p-1.5 space-y-2">
            {wave.courseBuckets.map(({ bucket, items }) => (
              <div key={bucket.id}>
                <div className="flex items-center gap-2 mb-1 px-1">
                  <span className="text-kds-badge uppercase tracking-widest text-kds-muted">
                    {bucket.name}
                  </span>
                  <div className="h-px flex-1 bg-kds-muted/20" />
                </div>
                <div className="space-y-1.5">{rows(items)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {showServed && servedItems.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-1 mt-1">
            <div className="h-px flex-1 bg-kds-muted/20" />
            <span className="text-kds-badge uppercase tracking-widest text-kds-muted">{t('kds.table.servedHeading', 'Serviti')}</span>
            <div className="h-px flex-1 bg-kds-muted/20" />
          </div>
          <div className="space-y-1.5">{rows(servedItems, true)}</div>
        </div>
      )}
    </>
  );
}
